import { CANVAS_WIDTH } from "../../game/constants";
import type { UiRects } from "../../game/input";
import type { RenderModel } from "../renderModel";
import { GAME_FONT } from "../renderConstants";

// ── Dependency interface ────────────────────────────────────────────

export interface OnboardingOverlayDeps {
  ctx: CanvasRenderingContext2D;
  canvasHeight: number;
  nowMs: () => number;
  rafsbyPhoneImage: HTMLImageElement | null;
  phoneImage: HTMLImageElement | null;
}

// ── Extracted screen function ───────────────────────────────────────

export function drawOnboardingOverlayScreen(
  deps: OnboardingOverlayDeps,
  model: RenderModel,
  uiRects: UiRects,
): void {
  const { ctx, canvasHeight, nowMs, rafsbyPhoneImage, phoneImage } = deps;
  const ob = model.onboardingState;
  const step = model.onboardingStep;
  if (!ob?.active || !step) return;

  const canvasH = canvasHeight;
  const phoneBtnSize = 180;
  const phoneBtnBaseX = CANVAS_WIDTH - phoneBtnSize + 10;
  const phoneBtnY = canvasH - phoneBtnSize + 10;

  // Draw Rafsby phone image
  const phoneImg = rafsbyPhoneImage ?? phoneImage;
  if (phoneImg) {
    const aspect = phoneImg.naturalWidth / phoneImg.naturalHeight;
    let drawW: number, drawH: number;
    if (aspect < 1) { drawH = phoneBtnSize; drawW = phoneBtnSize * aspect; }
    else { drawW = phoneBtnSize; drawH = phoneBtnSize / aspect; }
    const drawX = phoneBtnBaseX + (phoneBtnSize - drawW);
    const drawY = phoneBtnY + (phoneBtnSize - drawH);
    ctx.drawImage(phoneImg, drawX, drawY, drawW, drawH);
  }

  // Speech bubble
  const px = 2;
  const bubbleW = Math.min(130, phoneBtnBaseX);
  const pad = 8;
  const senderH = 10;
  const lineH = 8;
  const borderColor = "#1e3a5f";
  const fillColor = "#101d31";

  const charsPerLine = 22;
  const words = step.message.split(" ");
  const messageLines: string[] = [];
  let curLine = "";
  for (const w of words) {
    const test = curLine.length > 0 ? `${curLine} ${w}` : w;
    if (test.length > charsPerLine && curLine.length > 0) {
      messageLines.push(curLine);
      curLine = w;
    } else {
      curLine = test;
    }
  }
  if (curLine.length > 0) messageLines.push(curLine);

  const isReturnStep = step.type === "return-to-city";
  const instructionH = lineH + 4;
  const bubbleH = pad + senderH + messageLines.length * lineH + instructionH + pad;
  const bubbleX = phoneBtnBaseX - bubbleW + 22;
  const bubbleY = phoneBtnY - bubbleH - 8;

  ctx.save();
  ctx.imageSmoothingEnabled = false;

  // Main body
  ctx.fillStyle = fillColor;
  ctx.fillRect(bubbleX + px, bubbleY, bubbleW - px * 2, bubbleH);
  ctx.fillRect(bubbleX, bubbleY + px, bubbleW, bubbleH - px * 2);

  // Pixel tail
  const tailX = bubbleX + bubbleW - 18;
  ctx.fillRect(tailX, bubbleY + bubbleH, px * 3, px);
  ctx.fillRect(tailX + px, bubbleY + bubbleH + px, px * 3, px);
  ctx.fillRect(tailX + px * 2, bubbleY + bubbleH + px * 2, px * 3, px);

  // Border
  ctx.fillStyle = borderColor;
  ctx.fillRect(bubbleX + px, bubbleY, bubbleW - px * 2, px);
  ctx.fillRect(bubbleX + px, bubbleY + bubbleH - px, tailX - bubbleX - px, px);
  ctx.fillRect(tailX + px * 3, bubbleY + bubbleH - px, bubbleX + bubbleW - px - (tailX + px * 3), px);
  ctx.fillRect(bubbleX, bubbleY + px, px, bubbleH - px * 2);
  ctx.fillRect(bubbleX + bubbleW - px, bubbleY + px, px, bubbleH - px * 2);
  ctx.fillRect(tailX, bubbleY + bubbleH, px, px);
  ctx.fillRect(tailX + px, bubbleY + bubbleH + px, px, px);
  ctx.fillRect(tailX + px * 2, bubbleY + bubbleH + px * 2, px, px);
  ctx.fillRect(tailX + px * 3 + px * 2, bubbleY + bubbleH, px, px);
  ctx.fillRect(tailX + px * 3, bubbleY + bubbleH + px * 2, px * 2, px);

  ctx.restore();

  // Sender name
  ctx.fillStyle = "#4dff88";
  ctx.font = `bold 6px ${GAME_FONT}`;
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillText("RAFSBY", bubbleX + pad, bubbleY + pad);

  // Message text
  ctx.fillStyle = "#c0cfe0";
  ctx.font = `5px ${GAME_FONT}`;
  let textY = bubbleY + pad + senderH;
  for (const ln of messageLines) {
    ctx.fillText(ln, bubbleX + pad, textY);
    textY += lineH;
  }

  // Instruction
  const tapAlpha = 0.4 + Math.sin(nowMs() / 400) * 0.3;
  ctx.fillStyle = isReturnStep
    ? `rgba(77,255,136,${tapAlpha})`
    : `rgba(255,255,255,${tapAlpha})`;
  ctx.font = `bold 4px ${GAME_FONT}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.fillText(
    isReturnStep ? "\u25C0 TAP TO GO BACK" : "TAP TO CONTINUE",
    bubbleX + bubbleW / 2, textY + 2
  );

  // Tap zone = bubble
  uiRects.onboardingTapZone = { x: bubbleX, y: bubbleY, w: bubbleW, h: bubbleH + px * 3 };

  // Skip button
  const skipW = 36;
  const skipH = 12;
  const skipX = bubbleX + bubbleW / 2 - skipW / 2;
  const skipBottom = bubbleY + bubbleH + px * 3 + 2;
  ctx.fillStyle = "rgba(255,255,255,0.12)";
  ctx.beginPath();
  ctx.roundRect(skipX, skipBottom, skipW, skipH, 2);
  ctx.fill();
  ctx.fillStyle = "rgba(255,255,255,0.4)";
  ctx.font = `bold 4px ${GAME_FONT}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("SKIP", skipX + skipW / 2, skipBottom + skipH / 2);
  uiRects.onboardingSkip = { x: skipX, y: skipBottom, w: skipW, h: skipH };
}
